// src/hooks/useFaceRecognition.js
// ─────────────────────────────────────────────────────────────────────────────
// Wraps face-api.js for:
//   1. Loading models from /public/models
//   2. Enrolling a student (capture N face descriptors)
//   3. Identifying a face from a live video frame
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from "react";
import * as faceapi from "face-api.js";

const MODEL_URL = window.location.origin + "/models"; // Place models in /public/models/
const REQUIRED_MODELS = [
  "tiny_face_detector",
  "face_recognition_net",
  "face_landmark_68_tiny_net",
];

// Download models from:
// https://github.com/justadudewhohacks/face-api.js/tree/master/weights
// Required files per model (copy all .bin shards + manifest .json):
//   tiny_face_detector_model-*
//   face_recognition_net-*
//   face_landmark_68_tiny_model-*

const DETECTOR_OPTIONS = new faceapi.TinyFaceDetectorOptions({
  inputSize: 416,   // Increased from 320 for better accuracy
  scoreThreshold: 0.5,
});

export function useFaceRecognition() {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const labeledDescriptors              = useRef([]);   // LabeledFaceDescriptors[]
  const intervalRef                     = useRef(null);

  // ── Load models on mount ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
        ]);
        if (!cancelled) setModelsLoaded(true);
      } catch (e) {
        if (!cancelled) setError("Failed to load face models: " + e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Load enrolled students into matcher ───────────────────────────────
  const loadEnrolledStudents = useCallback(async (students) => {
    // students: [{ id, name, faceDescriptors: Float32Array[] }]
    labeledDescriptors.current = students
      .filter(s => s.faceDescriptors?.length > 0)
      .map(s => new faceapi.LabeledFaceDescriptors(
        s.id,
        s.faceDescriptors.map(d => new Float32Array(d))
      ));
  }, []);

  // ── Capture face descriptors for enrollment ───────────────────────────
  // Returns array of Float32Array descriptors (one per captured sample)
  const captureDescriptors = useCallback(async (videoEl, numSamples = 5, onProgress) => {
    if (!modelsLoaded) throw new Error("Models not loaded yet");
    const descriptors = [];

    for (let i = 0; i < numSamples; i++) {
      const detection = await faceapi
        .detectSingleFace(videoEl, DETECTOR_OPTIONS)
        .withFaceLandmarks(true)
        .withFaceDescriptor();

      if (!detection) {
        // Retry this sample once
        await sleep(500);
        const retry = await faceapi
          .detectSingleFace(videoEl, DETECTOR_OPTIONS)
          .withFaceLandmarks(true)
          .withFaceDescriptor();
        if (!retry) throw new Error(`No face detected for sample ${i + 1}. Keep your face centred.`);
        descriptors.push(retry.descriptor);
      } else {
        descriptors.push(detection.descriptor);
      }

      onProgress?.(i + 1, numSamples);
      await sleep(600);
    }
    return descriptors;
  }, [modelsLoaded]);

  // ── Identify a face from video frame ─────────────────────────────────
  // Returns { studentId, distance, confidence } | null
  const identifyFace = useCallback(async (videoEl) => {
    if (!modelsLoaded)                         return null;
    if (labeledDescriptors.current.length === 0) return null;

    const detection = await faceapi
      .detectSingleFace(videoEl, DETECTOR_OPTIONS)
      .withFaceLandmarks(true)
      .withFaceDescriptor();

    if (!detection) return null;

    const matcher = new faceapi.FaceMatcher(labeledDescriptors.current, 0.38);
    const match   = matcher.findBestMatch(detection.descriptor);

    if (match.label === "unknown") return null;

    return {
      studentId:  match.label,
      distance:   match.distance,
      confidence: Math.round((1 - match.distance) * 100),
      box:        detection.detection.box,
    };
  }, [modelsLoaded]);

  // ── Start continuous recognition loop ────────────────────────────────
  // Calls onMatch(result) whenever a face is recognised
  // Returns a stop function
  const startRecognitionLoop = useCallback((videoEl, onMatch, intervalMs = 1200) => {
    stopRecognitionLoop();
    intervalRef.current = setInterval(async () => {
      try {
        const result = await identifyFace(videoEl);
        if (result) onMatch(result);
      } catch { /* ignore single-frame errors */ }
    }, intervalMs);
    return stopRecognitionLoop;
  }, [identifyFace]);

  const stopRecognitionLoop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // ── Draw detection overlay on canvas ─────────────────────────────────
  const drawDetections = useCallback(async (videoEl, canvasEl) => {
    if (!videoEl || !canvasEl) return;
    if (videoEl.videoWidth === 0 || videoEl.videoHeight === 0) return;

    try {
      const detections = await faceapi
        .detectAllFaces(videoEl, DETECTOR_OPTIONS)
        .withFaceLandmarks(true);

      if (!detections || detections.length === 0) {
        const ctx = canvasEl.getContext("2d");
        ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
        return;
      }

      const dims = faceapi.matchDimensions(canvasEl, videoEl);
      const resized = faceapi.resizeResults(detections, dims);
      faceapi.draw.drawDetections(canvasEl, resized);
    } catch (err) {
      console.warn("Face detection draw error:", err);
    }
  }, []);

  return {
    modelsLoaded,
    loading,
    error,
    loadEnrolledStudents,
    captureDescriptors,
    identifyFace,
    startRecognitionLoop,
    stopRecognitionLoop,
    drawDetections,
  };
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }
